import { iconEnumToString } from "@storytell/bsky-icons";
import {
  type Icons,
  Label,
  timestampToDateOptional,
  useCallOptions,
  useLabelClient,
  withBearerToken,
} from "@storytell/cp-client";
import type { AssetId, LabelId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { TbCircleCheck } from "solid-icons/tb";
import { type Component, batch, createSignal } from "solid-js";
import { useWire } from "~/wire";
import { LabelPicker } from "./LabelPicker";

export const BulkAssetLabelPicker: Component<{
  mount?: Node;
  selected: AssetId[];
  onClose: () => void;
  onLabelCreated: (label: Label.Label) => void;
}> = (props) => {
  const wire = useWire();
  const [loading, setLoading] = createSignal(false);

  const labelsInCommon = () => {
    let workingSet: Set<string> | undefined;
    for (const id of props.selected) {
      const labels = wire.sync.getAssetRelatedLabels(id);
      if (!workingSet) {
        workingSet = new Set(labels);
        continue;
      }
      workingSet = workingSet.intersection(new Set(labels));
    }
    return Array.from(workingSet ?? []);
  };

  const partialLabels = () => {
    const labelCounts = new Map<string, number>();
    for (const id of props.selected) {
      const labels = wire.sync.getAssetRelatedLabels(id);
      for (const labelId of labels) {
        labelCounts.set(labelId, (labelCounts.get(labelId) || 0) + 1);
      }
    }
    // Return labels that are present on some but not all assets
    return Array.from(labelCounts.entries())
      .filter(([_, count]) => count > 0 && count < props.selected.length)
      .map(([labelId]) => labelId);
  };

  const [search, setSearch] = createSignal("");
  const [labels, setLabels] = createSignal<string[]>(labelsInCommon());
  const [explicitlyChanged, setExplicitlyChanged] = createSignal<Record<string, boolean>>({});

  // For display purposes, filter out partial labels that have been explicitly changed
  const displayPartialLabels = () => {
    const partial = partialLabels();
    const changed = explicitlyChanged();
    return partial.filter((labelId) => !changed[labelId]);
  };

  const onApply = async () => {
    setLoading(true);
    try {
      const client = useLabelClient();
      const explicitlyChangedLabels = Object.entries(explicitlyChanged())
        .filter(([_, value]) => value)
        .map(([id]) => id);
      const labelsToAdd = new Set(explicitlyChangedLabels.filter((l) => labels().includes(l)));
      const labelsToRemove = new Set(explicitlyChangedLabels.filter((l) => !labels().includes(l)));
      const ops = [];
      if (labelsToAdd.size > 0) {
        ops.push(
          client.bulkAssociateLabels(
            {
              labelIds: Array.from(labelsToAdd),
              resources: props.selected.map((id) => ({ resourceType: "asset", resourceId: id })),
            },
            useCallOptions(withBearerToken(wire.auth.token())),
          ),
        );
      }
      if (labelsToRemove.size > 0) {
        ops.push(
          client.bulkDisassociateLabels(
            {
              labelIds: Array.from(labelsToRemove),
              resources: props.selected.map((id) => ({ resourceType: "asset", resourceId: id })),
            },
            useCallOptions(withBearerToken(wire.auth.token())),
          ),
        );
      }

      await Promise.all(ops);

      batch(() => {
        for (const id of props.selected) {
          for (const labelId of explicitlyChangedLabels) {
            if (labelsToAdd.has(labelId)) {
              wire.sync.addLabelToAsset(id, labelId as LabelId);
            } else {
              wire.sync.removeLabelFromAsset(id, labelId as LabelId);
            }
          }
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      props.onClose();
    }
  };

  const client = useLabelClient();
  const onCreateLabel = async (data: {
    name: string;
    description: string;
    icon: Icons.Icon;
    color: string;
  }) => {
    try {
      const callOptions = useCallOptions(withBearerToken(wire.auth.token()));
      const result = await client.createLabel(
        {
          displayName: data.name,
          description: data.description,
          color: data.color,
          icon: data.icon,
          scope: Label.LabelScope.PROJECT,
          organizationId: wire.auth.realm()?.organizationId,
          projectId: wire.auth.realm()?.projectId,
          workspaceId: wire.auth.realm()?.workspaceId,
          tenantId: wire.auth.realm()?.tenantId,
        },
        callOptions,
      );

      if (result.isOk()) {
        const label = result.value.label;
        wire.sync.labels.set(label.id as LabelId, {
          id: label.id as LabelId,
          name: label.displayName,
          description: label.description,
          icon: iconEnumToString(label.icon),
          color: data.color,
          scope:
            Object.entries(Label.LabelScope).find(([, value]) => value === label.scope)?.[0] || "LABEL_SCOPE_PROJECT",
          usageCount: Number(label.usageCount),
          lastUsedAt: timestampToDateOptional(label.lastUsedAt)?.toISOString(),
          tenantId: label.tenantId,
          organizationId: label.organizationId || "",
          workspaceId: label.workspaceId || "",
          projectId: label.projectId || "",
          createdBy: label.auditMetadata?.created.userId || "",
          createdAt: timestampToDateOptional(label.createdAt)?.toISOString() ?? new Date().toISOString(),
          modifiedBy: label.auditMetadata?.modified.userId || "",
          modifiedAt: timestampToDateOptional(label.updatedAt)?.toISOString() ?? new Date().toISOString(),
          isArchived: false,
          userId: label.auditMetadata?.created.userId || "",
        });
        await client.bulkAssociateLabels(
          {
            labelIds: Array.from([result.value.label.id]),
            resources: props.selected.map((id) => ({ resourceType: "asset", resourceId: id })),
          },
          useCallOptions(withBearerToken(wire.auth.token())),
        );
        props.onLabelCreated(result.value.label);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <LabelPicker
        mount={props.mount}
        search={search()}
        setSearch={setSearch}
        labels={wire.sync.labels.search(search())}
        selectedLabels={labels()}
        partialLabels={displayPartialLabels()}
        title={`Change labels for ${props.selected.length} asset${props.selected.length === 1 ? "" : "s"}`}
        onSelectLabel={(l) => {
          setLabels((prev) => [...prev, l.id]);
          setExplicitlyChanged((prev) => ({ ...prev, [l.id]: true }));
        }}
        onDeselectLabel={(l) => {
          setLabels((prev) => prev.filter((id) => id !== l.id));
          setExplicitlyChanged((prev) => ({ ...prev, [l.id]: true }));
        }}
        onCreateLabel={onCreateLabel}
      />

      <StButton
        disabled={loading()}
        loading={loading()}
        icon={TbCircleCheck}
        noColor
        class="mb-3 ml-auto bg-brand hover:bg-brand-hover text-on-brand"
        onClick={onApply}
      >
        Apply changes
      </StButton>
    </>
  );
};
